import { Baby, RotateCcw, Search, Sparkles, Tag, UserMinus, Users, UserX } from 'lucide-react';
import { ALTER_MAX, ARTEN, alterText, istGefiltert, type FilterWert, type GruppenArt } from './gruppenInfo';
import './GruppenFilter.css';

type Props = {
  id: string;
  wert: FilterWert;
  onChange: (f: FilterWert) => void;
  /** Standard zum Zurücksetzen (im Assistenten: die Sportschulen der Mail) */
  standard: FilterWert;
  /** Sportschulen, die zur Wahl stehen */
  schulen: string[];
  /** Übergreifende Gruppen (ohne Sportschule) anbieten */
  mitUebergreifend: boolean;
  /** Arten, die es hier gibt */
  arten: GruppenArt[];
  treffer: number;
  gesamt: number;
  /** Wie viele Gruppen der Altersfilter ausblendet, weil sie keine Altersangabe haben */
  ohneAlter?: number;
};

/** Der Regler reicht bis 30 — am rechten Anschlag heißt es „und älter". So bleibt er für Kinder- und Jugendgruppen fein genug. */
const REGLER_MAX = 30;
const aufRegler = (a: number) => Math.min(a, REGLER_MAX);

const SCHNELL: { id: string; label: string; Icon: typeof Users; setzt: Partial<FilterWert>; art: GruppenArt }[] = [
  { id: 'mitglieder', label: 'Alle Mitglieder', Icon: Users, setzt: { arten: ['mitglieder'], alter: null }, art: 'mitglieder' },
  { id: 'familien', label: 'Eltern & Kinder', Icon: Baby, setzt: { arten: ['familien'], alter: null }, art: 'familien' },
  { id: 'alter', label: 'Kinder in einem Alter', Icon: Sparkles, setzt: { arten: ['familien'], alter: [6, 13] }, art: 'familien' },
  { id: 'tag', label: 'Trainingsgruppe / Kader (CRM-Tag)', Icon: Tag, setzt: { arten: ['tag'], alter: null }, art: 'tag' },
  { id: 'nicht-zahlaktiv', label: 'Nicht zahlaktiv', Icon: UserMinus, setzt: { arten: ['nicht-zahlaktiv'], alter: null }, art: 'nicht-zahlaktiv' },
  { id: 'ehemalige', label: 'Ehemalige', Icon: UserX, setzt: { arten: ['ehemalige'], alter: null }, art: 'ehemalige' },
];

/**
 * Suche und Filter für Empfängergruppen: Schnellwahl „Wen willst Du
 * erreichen?", Sportschule, Art und Alter. Alle Knöpfe sind Umschalter
 * (aria-pressed), der Altersregler besteht aus zwei normalen Schiebereglern.
 */
export function GruppenFilter({ id, wert, onChange, standard, schulen, mitUebergreifend, arten, treffer, gesamt, ohneAlter = 0 }: Props) {
  const setzen = (teil: Partial<FilterWert>) => onChange({ ...wert, ...teil });
  const umschalten = <T,>(liste: T[], x: T): T[] => (liste.includes(x) ? liste.filter((y) => y !== x) : [...liste, x]);
  const schnell = SCHNELL.filter((s) => arten.includes(s.art));
  const alter = wert.alter;

  return (
    <div className="gf" role="search" aria-label="Gruppen suchen und filtern">
      <div className="gf-kopf">
        <div className="gf-suche">
          <Search size={15} aria-hidden="true" />
          <input
            id={`${id}-suche`}
            className="form-input"
            type="search"
            placeholder="Gruppe suchen, z. B. Kader, Tegel, 6–13"
            value={wert.suche}
            onChange={(e) => setzen({ suche: e.target.value })}
            aria-label="Gruppe suchen"
          />
        </div>
        <span className="gf-treffer" aria-live="polite">{treffer} von {gesamt} Gruppen</span>
        {istGefiltert(wert, standard) && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(standard)}>
            <RotateCcw size={14} aria-hidden="true" /> Zurücksetzen
          </button>
        )}
      </div>

      {schnell.length > 0 && (
        <div className="gf-zeile" role="group" aria-label="Wen willst Du erreichen?">
          <span className="gf-label">Wen willst Du erreichen?</span>
          {schnell.map((s) => {
            const aktiv = wert.arten.length === 1 && wert.arten[0] === s.art && (s.id === 'alter' ? !!alter : s.art !== 'familien' || !alter);
            return (
              <button key={s.id} type="button" className={`chip gf-schnell${aktiv ? ' active' : ''}`} aria-pressed={aktiv}
                onClick={() => setzen(aktiv ? { arten: [], alter: null } : s.setzt)}>
                <s.Icon size={13} aria-hidden="true" /> {s.label}
              </button>
            );
          })}
        </div>
      )}

      {(schulen.length > 1 || mitUebergreifend) && (
        <div className="gf-zeile" role="group" aria-label="Sportschule">
          <span className="gf-label">Sportschule</span>
          {schulen.length > 1 && (
            <button type="button" className={`chip${wert.schulen.length === 0 ? ' active' : ''}`} aria-pressed={wert.schulen.length === 0}
              onClick={() => setzen({ schulen: [] })}>Alle</button>
          )}
          {schulen.length > 1 && schulen.map((s) => (
            <button key={s} type="button" className={`chip${wert.schulen.includes(s) ? ' active' : ''}`} aria-pressed={wert.schulen.includes(s)}
              onClick={() => setzen({ schulen: umschalten(wert.schulen, s) })}>{s}</button>
          ))}
          {mitUebergreifend && (
            <button type="button" className={`chip${wert.uebergreifend ? ' active' : ''}`} aria-pressed={wert.uebergreifend}
              onClick={() => setzen({ uebergreifend: !wert.uebergreifend })}>Übergreifend</button>
          )}
        </div>
      )}

      <div className="gf-zeile" role="group" aria-label="Art der Gruppe">
        <span className="gf-label">Art</span>
        {ARTEN.filter((a) => arten.includes(a.id)).map((a) => (
          <button key={a.id} type="button" title={a.text} className={`chip${wert.arten.includes(a.id) ? ' active' : ''}`} aria-pressed={wert.arten.includes(a.id)}
            onClick={() => setzen({ arten: umschalten(wert.arten, a.id) })}>{a.label}</button>
        ))}
      </div>

      <div className="gf-zeile gf-alter">
        <label className="gf-label gf-alter-an">
          <input type="checkbox" checked={!!alter} onChange={(e) => setzen({ alter: e.target.checked ? [6, 13] : null })} />
          Nach Alter
        </label>
        {alter && (
          <>
            <div className="gf-regler" style={{ '--von': `${(aufRegler(alter[0]) / REGLER_MAX) * 100}%`, '--bis': `${(aufRegler(alter[1]) / REGLER_MAX) * 100}%` } as React.CSSProperties}>
              <input type="range" min={0} max={REGLER_MAX} value={aufRegler(alter[0])} aria-label="Alter von" aria-valuetext={`${alter[0]} Jahre`}
                onChange={(e) => setzen({ alter: [Math.min(Number(e.target.value), alter[1]), alter[1]] })} />
              <input type="range" min={0} max={REGLER_MAX} value={aufRegler(alter[1])} aria-label="Alter bis"
                aria-valuetext={alter[1] >= ALTER_MAX ? 'und älter' : `${alter[1]} Jahre`}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setzen({ alter: [alter[0], Math.max(v >= REGLER_MAX ? ALTER_MAX : v, alter[0])] });
                }} />
            </div>
            <span className="gf-alter-wert">{alterText(alter)}</span>
            {ohneAlter > 0 && <span className="gf-hinweis">{ohneAlter} {ohneAlter === 1 ? 'Gruppe' : 'Gruppen'} ohne Altersangabe ausgeblendet</span>}
          </>
        )}
      </div>
    </div>
  );
}
